import { useState, useCallback } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import {
  Copy,
  Check,
  RefreshCw,
  Palette,
  Sun,
  Hash,
  Sliders,
  Layers,
  Code,
  AlertCircle
} from 'lucide-react'

// Color conversion utilities
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const clean = hex.replace('#', '')
  if (clean.length === 3) {
    const r = parseInt(clean[0] + clean[0], 16)
    const g = parseInt(clean[1] + clean[1], 16)
    const b = parseInt(clean[2] + clean[2], 16)
    return { r, g, b }
  }
  if (clean.length === 6) {
    const r = parseInt(clean.substring(0, 2), 16)
    const g = parseInt(clean.substring(2, 4), 16)
    const b = parseInt(clean.substring(4, 6), 16)
    return { r, g, b }
  }
  return null
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let h = 0, s = 0, l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) }
}

function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360; s /= 100; l /= 100
  let r = l, g = l, b = l

  if (s !== 0) {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    const hueToRgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1/6) return p + (q - p) * 6 * t
      if (t < 1/2) return q
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
      return p
    }
    r = hueToRgb(p, q, h + 1/3)
    g = hueToRgb(p, q, h)
    b = hueToRgb(p, q, h - 1/3)
  }
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) }
}

function rgbToCmyk(r: number, g: number, b: number): { c: number; m: number; y: number; k: number } {
  const rp = r / 255, gp = g / 255, bp = b / 255
  const k = 1 - Math.max(rp, gp, bp)
  const c = k === 1 ? 0 : (1 - rp - k) / (1 - k)
  const m = k === 1 ? 0 : (1 - gp - k) / (1 - k)
  const y = k === 1 ? 0 : (1 - bp - k) / (1 - k)
  return { c: Math.round(c * 100), m: Math.round(m * 100), y: Math.round(y * 100), k: Math.round(k * 100) }
}

function cmykToRgb(c: number, m: number, y: number, k: number): { r: number; g: number; b: number } {
  const r = 255 * (1 - c / 100) * (1 - k / 100)
  const g = 255 * (1 - m / 100) * (1 - k / 100)
  const b = 255 * (1 - y / 100) * (1 - k / 100)
  return { r: Math.round(r), g: Math.round(g), b: Math.round(b) }
}

// Contrast ratio
function getLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c /= 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

function getContrastRatio(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
  const l1 = getLuminance(r1, g1, b1)
  const l2 = getLuminance(r2, g2, b2)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

// Generate color harmonies
function getHarmonies(h: number, _s: number, _l: number) {
  return {
    complementary: (h + 180) % 360,
    analogous1: (h + 30) % 360,
    analogous2: (h - 30 + 360) % 360,
    triadic1: (h + 120) % 360,
    triadic2: (h + 240) % 360,
    split1: (h + 150) % 360,
    split2: (h + 210) % 360,
  }
}

function App() {
  const { t } = useI18n()
  const [hex, setHex] = useState('#6366F1')
  const [rgb, setRgb] = useState({ r: 99, g: 102, b: 241 })
  const [hsl, setHsl] = useState({ h: 239, s: 84, l: 67 })
  const [cmyk, setCmyk] = useState({ c: 59, m: 58, y: 0, k: 5 })
  const [copied, setCopied] = useState<string | null>(null)
  const [error, setError] = useState('')

  // Sync all formats when one changes
  const updateFromHex = useCallback((newHex: string) => {
    if (!newHex.startsWith('#')) newHex = '#' + newHex
    const rgbVal = hexToRgb(newHex)
    if (!rgbVal) {
      setError(t('color.invalidHex') || 'Invalid HEX color')
      return
    }
    setError('')
    setHex(newHex.toUpperCase())
    setRgb(rgbVal)
    setHsl(rgbToHsl(rgbVal.r, rgbVal.g, rgbVal.b))
    setCmyk(rgbToCmyk(rgbVal.r, rgbVal.g, rgbVal.b))
  }, [t])

  const updateFromRgb = useCallback((newRgb: { r: number; g: number; b: number }) => {
    setError('')
    setRgb(newRgb)
    const newHex = rgbToHex(newRgb.r, newRgb.g, newRgb.b)
    setHex(newHex)
    setHsl(rgbToHsl(newRgb.r, newRgb.g, newRgb.b))
    setCmyk(rgbToCmyk(newRgb.r, newRgb.g, newRgb.b))
  }, [])

  const updateFromHsl = useCallback((newHsl: { h: number; s: number; l: number }) => {
    setError('')
    setHsl(newHsl)
    const rgbVal = hslToRgb(newHsl.h, newHsl.s, newHsl.l)
    setRgb(rgbVal)
    const newHex = rgbToHex(rgbVal.r, rgbVal.g, rgbVal.b)
    setHex(newHex)
    setCmyk(rgbToCmyk(rgbVal.r, rgbVal.g, rgbVal.b))
  }, [])

  const updateFromCmyk = useCallback((newCmyk: { c: number; m: number; y: number; k: number }) => {
    setError('')
    setCmyk(newCmyk)
    const rgbVal = cmykToRgb(newCmyk.c, newCmyk.m, newCmyk.y, newCmyk.k)
    setRgb(rgbVal)
    const newHex = rgbToHex(rgbVal.r, rgbVal.g, rgbVal.b)
    setHex(newHex)
    setHsl(rgbToHsl(rgbVal.r, rgbVal.g, rgbVal.b))
  }, [])

  const copyToClipboard = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(label)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      // Fallback
    }
  }, [])

  const randomColor = useCallback(() => {
    const r = Math.floor(Math.random() * 256)
    const g = Math.floor(Math.random() * 256)
    const b = Math.floor(Math.random() * 256)
    updateFromRgb({ r, g, b })
  }, [updateFromRgb])

  // Contrast ratios
  const contrastWhite = getContrastRatio(rgb.r, rgb.g, rgb.b, 255, 255, 255)
  const contrastBlack = getContrastRatio(rgb.r, rgb.g, rgb.b, 0, 0, 0)
  const betterText = contrastWhite >= contrastBlack ? '#FFFFFF' : '#000000'

  // Harmonies
  const harmonies = getHarmonies(hsl.h, hsl.s, hsl.l)

  const cssFormats = [
    { label: 'HEX', value: hex },
    { label: 'RGB', value: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` },
    { label: 'RGBA', value: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)` },
    { label: 'HSL', value: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` },
    { label: 'CMYK', value: `cmyk(${cmyk.c}%, ${cmyk.m}%, ${cmyk.y}%, ${cmyk.k}%)` },
    { label: 'CSS Var', value: `--color: ${hex};` },
  ]

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('colorConverter.title') || 'Color Converter'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('colorConverter.subtitle') || 'Convert between HEX, RGB, HSL, CMYK. Check contrast, explore harmonies. '}
          <span className="text-primary font-medium">{t('colorConverter.privacy') || '100% client-side'}</span>.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Color Preview */}
        <div
          className="rounded-xl shadow-sm border border-border overflow-hidden"
          style={{ backgroundColor: hex }}
        >
          <div className="p-8 md:p-12 text-center" style={{ color: betterText }}>
            <div className="text-4xl md:text-5xl font-bold mb-2 font-mono">{hex}</div>
            <div className="text-lg opacity-80 font-mono">
              rgb({rgb.r}, {rgb.g}, {rgb.b})
            </div>
            <div className="mt-4 flex justify-center gap-2">
              <button
                onClick={randomColor}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-opacity opacity-80 hover:opacity-100"
                style={{ backgroundColor: betterText === '#FFFFFF' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)', color: betterText }}
              >
                <RefreshCw className="w-4 h-4" />
                {t('colorConverter.random') || 'Random'}
              </button>
              <button
                onClick={() => copyToClipboard(hex, 'hex')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-opacity opacity-80 hover:opacity-100"
                style={{ backgroundColor: betterText === '#FFFFFF' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)', color: betterText }}
              >
                {copied === 'hex' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied === 'hex' ? (t('actions.copied') || 'Copied!') : (t('actions.copy') || 'Copy')}
              </button>
            </div>
          </div>
        </div>

        {/* Input Panels */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* HEX */}
          <div className="bg-surface rounded-xl shadow-sm border border-border p-4">
            <div className="flex items-center gap-2 mb-3">
              <Hash className="w-4 h-4 text-brand-primary" />
              <h3 className="text-sm font-semibold text-foreground">HEX</h3>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={hex}
                  onChange={e => updateFromHex(e.target.value)}
                  className="w-10 h-10 rounded-lg border border-border cursor-pointer shrink-0"
                />
                <input
                  type="text"
                  value={hex}
                  onChange={e => updateFromHex(e.target.value)}
                  className="flex-1 px-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm font-mono outline-none focus:ring-2 focus:ring-brand-primary/50 uppercase"
                  placeholder="#FFFFFF"
                />
              </div>
            </div>
          </div>

          {/* RGB */}
          <div className="bg-surface rounded-xl shadow-sm border border-border p-4">
            <div className="flex items-center gap-2 mb-3">
              <Palette className="w-4 h-4 text-brand-primary" />
              <h3 className="text-sm font-semibold text-foreground">RGB</h3>
            </div>
            <div className="space-y-2">
              {(['r', 'g', 'b'] as const).map(channel => (
                <div key={channel} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-4 uppercase font-mono">{channel}</span>
                  <input
                    type="number"
                    min={0}
                    max={255}
                    value={rgb[channel]}
                    onChange={e => updateFromRgb({ ...rgb, [channel]: Number(e.target.value) })}
                    className="flex-1 px-3 py-1.5 bg-background border border-border rounded-lg text-foreground text-sm font-mono outline-none focus:ring-2 focus:ring-brand-primary/50"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* HSL */}
          <div className="bg-surface rounded-xl shadow-sm border border-border p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sliders className="w-4 h-4 text-brand-primary" />
              <h3 className="text-sm font-semibold text-foreground">HSL</h3>
            </div>
            <div className="space-y-2">
              {(['h', 's', 'l'] as const).map(key => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-4 uppercase font-mono">{key}</span>
                  <input
                    type="number"
                    min={0}
                    max={key === 'h' ? 360 : 100}
                    value={hsl[key]}
                    onChange={e => updateFromHsl({ ...hsl, [key]: Number(e.target.value) })}
                    className="flex-1 px-3 py-1.5 bg-background border border-border rounded-lg text-foreground text-sm font-mono outline-none focus:ring-2 focus:ring-brand-primary/50"
                  />
                  {key !== 'h' && <span className="text-xs text-muted-foreground">%</span>}
                </div>
              ))}
            </div>
          </div>

          {/* CMYK */}
          <div className="bg-surface rounded-xl shadow-sm border border-border p-4">
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-4 h-4 text-brand-primary" />
              <h3 className="text-sm font-semibold text-foreground">CMYK</h3>
            </div>
            <div className="space-y-2">
              {(['c', 'm', 'y', 'k'] as const).map(key => (
                <div key={key} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-4 uppercase font-mono">{key}</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={cmyk[key]}
                    onChange={e => updateFromCmyk({ ...cmyk, [key]: Number(e.target.value) })}
                    className="flex-1 px-3 py-1.5 bg-background border border-border rounded-lg text-foreground text-sm font-mono outline-none focus:ring-2 focus:ring-brand-primary/50"
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contrast Check */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Sun className="w-4 h-4 text-brand-primary" />
            {t('colorConverter.contrast') || 'Contrast Check (WCAG)'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* White text on color */}
            <div
              className="rounded-lg p-4 flex items-center justify-between"
              style={{ backgroundColor: hex, color: '#FFFFFF' }}
            >
              <div>
                <p className="text-lg font-medium">{t('colorConverter.whiteText') || 'White Text'}</p>
                <p className="text-sm opacity-80">Ratio: {contrastWhite.toFixed(2)}:1</p>
              </div>
              <div className="text-right">
                <p className={`text-sm font-medium ${contrastWhite >= 4.5 ? 'text-green-400' : 'text-red-400'}`}>
                  {contrastWhite >= 4.5 ? 'AA ✓' : 'AA ✗'}
                </p>
                <p className={`text-xs ${contrastWhite >= 7 ? 'text-green-400' : 'text-red-400'}`}>
                  {contrastWhite >= 7 ? 'AAA ✓' : 'AAA ✗'}
                </p>
              </div>
            </div>

            {/* Black text on color */}
            <div
              className="rounded-lg p-4 flex items-center justify-between"
              style={{ backgroundColor: hex, color: '#000000' }}
            >
              <div>
                <p className="text-lg font-medium">{t('colorConverter.blackText') || 'Black Text'}</p>
                <p className="text-sm opacity-80">Ratio: {contrastBlack.toFixed(2)}:1</p>
              </div>
              <div className="text-right">
                <p className={`text-sm font-medium ${contrastBlack >= 4.5 ? 'text-green-600' : 'text-red-600'}`}>
                  {contrastBlack >= 4.5 ? 'AA ✓' : 'AA ✗'}
                </p>
                <p className={`text-xs ${contrastBlack >= 7 ? 'text-green-600' : 'text-red-600'}`}>
                  {contrastBlack >= 7 ? 'AAA ✓' : 'AAA ✗'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Color Harmonies */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Palette className="w-4 h-4 text-brand-primary" />
            {t('colorConverter.harmonies') || 'Color Harmonies'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: t('colorConverter.original') || 'Original', hue: hsl.h },
              { label: t('colorConverter.complementary') || 'Complementary', hue: harmonies.complementary },
              { label: t('colorConverter.analogous1') || 'Analogous +30', hue: harmonies.analogous1 },
              { label: t('colorConverter.analogous2') || 'Analogous -30', hue: harmonies.analogous2 },
              { label: t('colorConverter.triadic1') || 'Triadic +120', hue: harmonies.triadic1 },
              { label: t('colorConverter.triadic2') || 'Triadic +240', hue: harmonies.triadic2 },
              { label: t('colorConverter.split') || 'Split Comp.', hue: harmonies.split1 },
            ].map(({ label, hue }) => {
              const harmonyRgb = hslToRgb(hue, hsl.s, hsl.l)
              const harmonyHex = rgbToHex(harmonyRgb.r, harmonyRgb.g, harmonyRgb.b)
              return (
                <button
                  key={label}
                  onClick={() => updateFromHex(harmonyHex)}
                  className="group text-left"
                >
                  <div
                    className="w-full aspect-square rounded-lg shadow-sm mb-2 transition-transform hover:scale-105"
                    style={{ backgroundColor: harmonyHex }}
                  />
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-xs font-mono text-foreground">{harmonyHex}</p>
                </button>
              )
            })}
          </div>
        </div>

        {/* CSS Export */}
        <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Code className="w-4 h-4 text-brand-primary" />
            {t('colorConverter.cssExport') || 'CSS Export'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {cssFormats.map(({ label, value }) => (
              <div
                key={label}
                className="flex items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg"
              >
                <span className="text-xs text-muted-foreground w-16 shrink-0">{label}</span>
                <code className="flex-1 text-xs font-mono text-foreground truncate">{value}</code>
                <button
                  onClick={() => copyToClipboard(value, label)}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors shrink-0"
                >
                  {copied === label ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            {t('security.note')}
          </p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
